import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import authStore from '../store/authStore';

const schema = z.object({
  email: z.string().email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

const totpSchema = z.object({
  code: z.string().min(6, 'Enter the 6-digit code or a backup code').max(9),
});

export default function Login() {
  const navigate = useNavigate();
  const { login, totpVerify } = authStore();
  const [mfaToken, setMfaToken] = useState(null);
  const [useBackupCode, setUseBackupCode] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ resolver: zodResolver(schema) });

  const totpForm = useForm({ resolver: zodResolver(totpSchema) });

  const onSubmit = async ({ email, password }) => {
    try {
      const result = await login(email, password);
      if (result?.requireTotp) {
        setMfaToken(result.mfaToken);
        return;
      }
      const currentUser = authStore.getState().user;
      navigate(currentUser?.role === 'client' ? '/portal' : '/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Login failed');
    }
  };

  const onTotpSubmit = async ({ code }) => {
    try {
      await totpVerify(mfaToken, code);
      const currentUser = authStore.getState().user;
      navigate(currentUser?.role === 'client' ? '/portal' : '/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid code');
    }
  };

  if (mfaToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <div className="max-w-md w-full card">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-primary-600 mb-2">Sage</h1>
            <p className="text-neutral-600 dark:text-neutral-400">Two-Factor Authentication</p>
          </div>
          <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-6 text-center">
            {useBackupCode
              ? 'Enter one of your backup codes (format: XXXX-XXXX).'
              : 'Open your authenticator app and enter the 6-digit code.'}
          </p>
          <form className="space-y-6" onSubmit={totpForm.handleSubmit(onTotpSubmit)}>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                {useBackupCode ? 'Backup code' : 'Authentication code'}
              </label>
              <input
                type="text"
                inputMode={useBackupCode ? 'text' : 'numeric'}
                autoComplete="one-time-code"
                maxLength={useBackupCode ? 9 : 6}
                className="input text-center tracking-widest text-lg"
                placeholder={useBackupCode ? 'XXXX-XXXX' : '000000'}
                {...totpForm.register('code')}
              />
              {totpForm.formState.errors.code && (
                <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{totpForm.formState.errors.code.message}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={totpForm.formState.isSubmitting}
              className="btn btn-primary w-full disabled:opacity-60"
            >
              {totpForm.formState.isSubmitting ? 'Verifying…' : 'Verify'}
            </button>
          </form>
          <div className="mt-4 text-center space-y-2">
            <button
              onClick={() => { setUseBackupCode((v) => !v); totpForm.reset(); }}
              className="block w-full text-sm text-primary-600 hover:text-primary-700"
            >
              {useBackupCode ? 'Use authenticator app instead' : 'Use a backup code instead'}
            </button>
            <button
              onClick={() => { setMfaToken(null); setUseBackupCode(false); }}
              className="text-sm text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200"
            >
              Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-50 dark:bg-neutral-900">
      <div className="max-w-md w-full card">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary-600 mb-2">Sage</h1>
          <p className="text-neutral-600 dark:text-neutral-400">Practice Management Platform</p>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Email</label>
            <input
              type="email"
              className="input"
              placeholder="you@example.com"
              {...register('email')}
            />
            {errors.email && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.email.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Password</label>
            <input
              type="password"
              className="input"
              placeholder="••••••••"
              {...register('password')}
            />
            {errors.password && <p className="mt-1 text-sm text-danger-600 dark:text-danger-400">{errors.password.message}</p>}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary w-full disabled:opacity-60"
          >
            {isSubmitting ? 'Signing in…' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center space-y-2">
          <Link to="/forgot-password" className="block text-sm text-primary-600 hover:text-primary-700">
            Forgot password?
          </Link>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">
            Don&apos;t have an account?{' '}
            <Link to="/register" className="text-primary-600 hover:text-primary-700 font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
